import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { formatDayMonthOnly } from "@/lib/format";
import type { Order } from "@/types/orders";

interface Props {
  order: Order;
  /** Closes the host dialog when the route link is followed. */
  onNavigate: () => void;
}

/**
 * «МАРШРУТ» — the box pinned to the BOTTOM of the third column (mockup,
 * 2026-08-25).
 *
 * It answers the one question the change log above it cannot: *is this order on
 * a route yet, and if not, when can it be?* An unassigned order says the date
 * it becomes available in planning, which is its delivery date — a dispatcher
 * reading «Не распределён» alone would have to go and look that up.
 *
 * The date is rendered with {@link formatDayMonthOnly}, not `formatDayMonth`:
 * `delivery_date` is a bare `YYYY-MM-DD`, and the plain parse reads it as UTC
 * midnight, which renders the PREVIOUS day in any negative-offset timezone
 * (the BUG-355 / BUG-491 family). The suite runs in UTC, so no test can see it.
 *
 * A missing delivery date is stated rather than interpolated into the sentence —
 * «доступен в планировании на Invalid Date» is the failure this avoids.
 */
export function OrderRouteBox({ order, onNavigate }: Props) {
  const { t } = useTranslation();
  const assigned = order.route_id != null;

  return (
    <section className="rounded-xl border border-gray-200 px-3 py-2.5">
      <h4 className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
        {t("orders.detail.routeBoxTitle")}
      </h4>
      {assigned ? (
        <Link
          to={`/routes/${order.route_id}`}
          onClick={onNavigate}
          className="mt-1 inline-block text-sm font-semibold text-brand-primary hover:underline"
        >
          {t("orders.detail.routeAssigned", { id: order.route_id })}
        </Link>
      ) : (
        <p className="mt-1 text-sm text-gray-600">
          {order.delivery_date
            ? t("orders.detail.routeUnassigned", {
                date: formatDayMonthOnly(order.delivery_date),
              })
            : t("orders.detail.routeUnassignedNoDate")}
        </p>
      )}
    </section>
  );
}
